import Box from '@liquidweb-dev/undercurrent/Box';
import Card from '@liquidweb-dev/undercurrent/Card';
import CardContent from '@liquidweb-dev/undercurrent/CardContent';
import CardLayeredFooter from '@liquidweb-dev/undercurrent/CardLayeredFooter';
import Collapse from '@liquidweb-dev/undercurrent/Collapse';
import Divider from '@liquidweb-dev/undercurrent/Divider';
import BadgeCheckIcon from '@liquidweb-dev/undercurrent/icons/lucide/BadgeCheck';
import CircleDollarSignIcon from '@liquidweb-dev/undercurrent/icons/lucide/CircleDollarSign';
import GlobeIcon from '@liquidweb-dev/undercurrent/icons/lucide/Globe';
import RefreshCwIcon from '@liquidweb-dev/undercurrent/icons/lucide/RefreshCw';
import Skeleton from '@liquidweb-dev/undercurrent/Skeleton';
import Stack, { StackProps } from '@liquidweb-dev/undercurrent/Stack';
import Tooltip from '@liquidweb-dev/undercurrent/Tooltip';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';

import { useSslDvFlatDetails } from 'modules/queries/product/useFlatDetails';
import isSanFeeExempt from 'modules/ssl/isSanFeeExempt';

const Wrapper = ({ children, sx, ...rest }: StackProps) => (
	<Stack sx={combineSx({ gap: 150 }, sx)} {...rest}>
		<Typography variant="label-lg" sx={{ fontWeight: 500 }}>
			Type
		</Typography>
		<Card sx={({ vars }) => ({ borderColor: vars.palette.uc.border.main })}>
			<CardContent>{children}</CardContent>
		</Card>
	</Stack>
);

export interface DetailProps {
	label: string;
	value: string;
}

const Detail = ({ label, value }: DetailProps) => (
	<Stack sx={{ gap: 50 }}>
		<Typography
			variant="label-md"
			sx={({ vars }) => ({ color: vars.palette.uc.text.weak })}
		>
			{label}
		</Typography>
		<Typography variant="body-sm">{value}</Typography>
	</Stack>
);

const createLocationLabel = (
	city?: string,
	state?: string,
	country?: string,
) => {
	const locationArr = [city, state];

	// don't need to show country if it's USA
	if (country !== 'US' && country !== 'United States' && country !== 'USA') {
		locationArr.push(country);
	}

	return locationArr.filter(Boolean).join(', ');
};

export interface TypeCardProps extends StackProps {
	autorenew: boolean;
	wildcard: boolean;
	commonName?: string;
	city?: string;
	state?: string;
	country?: string;
	emailAddress?: string;
	organization?: string;
	organizationalUnit?: string;
	san?: string[];
}

const TypeCard = ({
	autorenew,
	wildcard,
	commonName,
	city,
	state,
	country,
	emailAddress,
	organization,
	organizationalUnit,
	san: sanProp,
	...rest
}: TypeCardProps) => {
	const { data: sslDvProductData, isLoading } = useSslDvFlatDetails();

	if (isLoading) {
		return (
			<Wrapper {...rest}>
				<Skeleton
					variant="rounded"
					sx={({ size }) => ({ height: size.sm, width: '100%' })}
				/>
			</Wrapper>
		);
	}

	const location = createLocationLabel(city, state, country);

	const sslOptionValues = sslDvProductData?.optionValues;

	const durationOption = wildcard
		? sslOptionValues?.['DomainSSL.1.Wildcard']
		: sslOptionValues?.['DomainSSL.1'];

	const price = durationOption?.region_id_prices?.[1];

	const sanFee = sslDvProductData?.optionValues.SANs?.region_id_prices?.[1];
	const san = sanProp?.map((domain) => ({
		domain,
		fee: isSanFeeExempt(domain, commonName || '') ? 0 : sanFee,
	}));

	return (
		<Stack {...rest}>
			<Wrapper sx={{ zIndex: 1 }}>
				<Stack
					sx={{
						flexDirection: { sm: 'row' },
						gap: 200,
						alignItems: { sm: 'center' },
						justifyContent: 'space-between',
					}}
				>
					<Stack sx={{ flexDirection: 'row', gap: 100, alignItems: 'center' }}>
						<BadgeCheckIcon
							sx={({ size, vars }) => ({
								height: size.sm,
								width: size.sm,
								flexShrink: 0,
								color: vars.palette.uc.highlight.icon.main,
							})}
						/>
						<Typography variant="title-xs" sx={{ fontWeight: 500 }}>
							1 year DomainSSL Certificate{wildcard && ' - Wildcard'}
						</Typography>
					</Stack>

					<Stack
						sx={{
							flexDirection: 'row',
							gap: 200,
							alignItems: 'center',
							justifyContent: { xs: 'space-between', sm: 'normal' },
						}}
					>
						{autorenew && (
							<Stack
								sx={({ vars }) => ({
									flexDirection: 'row',
									gap: 50,
									alignItems: 'center',
									color: vars.palette.uc.success.text.main,
								})}
							>
								<RefreshCwIcon
									sx={({ size }) => ({
										height: size.xS,
										width: size.xS,
										flexShrink: 0,
									})}
								/>
								<Typography variant="body-sm">Will autorenew</Typography>
							</Stack>
						)}

						<Stack
							sx={({ vars }) => ({
								flexDirection: 'row',
								gap: 50,
								alignItems: 'center',
								color: vars.palette.uc.text.weak,
							})}
						>
							<CircleDollarSignIcon
								sx={({ size }) => ({
									height: size.xS,
									width: size.xS,
									flexShrink: 0,
								})}
							/>
							<Typography variant="body-sm">${price}</Typography>
						</Stack>
					</Stack>
				</Stack>

				<Collapse in={Boolean(commonName)}>
					<Divider
						sx={({ vars }) => ({
							backgroundColor: vars.palette.uc.border.main,
							marginY: 200,
						})}
					/>

					<Box
						sx={{
							display: 'grid',
							gap: 200,
							gridTemplateColumns: 'repeat(auto-fit, minmax(219px, 1fr))',
						}}
					>
						{commonName && <Detail label="Domain" value={commonName} />}
						<Detail label="Type" value={wildcard ? 'Wildcard' : 'Standard'} />
						{emailAddress && <Detail label="Email" value={emailAddress} />}
						{organization && <Detail label="Company" value={organization} />}
						{organizationalUnit && (
							<Detail label="Company Division" value={organizationalUnit} />
						)}
						{location && <Detail label="Location" value={location} />}
					</Box>
				</Collapse>
			</Wrapper>

			<Collapse in={san && san?.length > 0}>
				<CardLayeredFooter
					sx={({ vars }) => ({
						backgroundColor: vars.palette.uc.highlight.bg.main,
						marginTop: -150,
						paddingTop: 300,
						paddingBottom: 150,
						display: 'flex',
						justifyContent: 'space-between',
					})}
				>
					<Tooltip
						variant="fancy"
						title="How does it work?"
						message="A Subject Alternative Name added to secure multiple domains with one SSL"
						arrow
						placement="top"
					>
						<Typography sx={{ fontWeight: 500 }} tooltip>
							SAN domains
						</Typography>
					</Tooltip>

					<Stack sx={{ gap: 50 }}>
						{san &&
							san.length > 0 &&
							san.map(({ domain, fee }) => (
								<Stack
									key={domain}
									sx={{ flexDirection: 'row', gap: 200, alignItems: 'center' }}
								>
									<Stack
										sx={{ flexDirection: 'row', gap: 50, alignItems: 'center' }}
									>
										<GlobeIcon
											sx={({ size, vars }) => ({
												height: size.xS,
												width: size.xS,
												flexShrink: 0,
												color: vars.palette.uc.icon.weak,
											})}
										/>
										<Typography variant="body-sm">{domain}</Typography>
									</Stack>
									{typeof fee === 'number' && fee > 0 && (
										<Stack
											sx={{
												flexDirection: 'row',
												gap: 50,
												alignItems: 'center',
											}}
										>
											<CircleDollarSignIcon
												sx={({ size, vars }) => ({
													height: size.xS,
													width: size.xS,
													flexShrink: 0,
													color: vars.palette.uc.icon.weak,
												})}
											/>
											<Typography
												variant="body-sm"
												sx={({ vars }) => ({
													color: vars.palette.uc.text.weak,
												})}
											>
												+ ${fee}
											</Typography>
										</Stack>
									)}
								</Stack>
							))}
					</Stack>
				</CardLayeredFooter>
			</Collapse>
		</Stack>
	);
};

export default TypeCard;
